import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import MainURL from '../../MainURL';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import CompanySubnav from './CompanySubnav';
import { useCompanyReveal } from './useCompanyReveal';
import './companyShared.scss';
import './CompanyFAQ.scss';

// 질문은 최종관리자 콘솔 'CS 및 고객지원 > 자주 묻는 질문 관리'에서 등록하고 공개한 것만 보인다.
type Faq = {
  id: number;
  category: string;
  question: string;
  content: string;
  linkLabel: string;
  linkUrl: string;
};

const ALL = '전체';

function FaqLink({ faq }: { faq: Faq }) {
  if (!faq.linkUrl || !faq.linkLabel) return null;
  if (faq.linkUrl.startsWith('/')) {
    return (
      <Link to={faq.linkUrl} className="faq-link">
        {faq.linkLabel}
      </Link>
    );
  }
  return (
    <a href={faq.linkUrl} className="faq-link" target="_blank" rel="noopener noreferrer">
      {faq.linkLabel}
    </a>
  );
}

export default function CompanyFAQ() {
  useCompanyReveal();
  const [faqs, setFaqs] = useState<Faq[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [category, setCategory] = useState(ALL);
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get<{ ok: boolean; data?: Faq[] }>(`${MainURL}/admin-console/faqs`, { params: { published: 1 } })
      .then((res) => {
        if (cancelled) return;
        if (res.data?.ok && Array.isArray(res.data.data)) {
          setFaqs(res.data.data);
          setOpenId(res.data.data[0]?.id ?? null);
          setStatus('ready');
        } else {
          setStatus('error');
        }
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    faqs.forEach((faq) => counts.set(faq.category, (counts.get(faq.category) ?? 0) + 1));
    return [{ name: ALL, count: faqs.length }, ...Array.from(counts, ([name, count]) => ({ name, count }))];
  }, [faqs]);

  const filtered = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return faqs.filter(
      (faq) =>
        (category === ALL || faq.category === category) &&
        (!keyword ||
          faq.question.toLowerCase().includes(keyword) ||
          faq.content.toLowerCase().includes(keyword)),
    );
  }, [faqs, category, search]);

  return (
    <div className="company-page company-faq">
      <Header />
      <CompanySubnav />

      <section className="page-header">
        <div className="container reveal">
          <div className="breadcrumb">
            <Link to="/">Home</Link> <span>· Company · FAQ</span>
          </div>
          <div className="page-header-grid">
            <div>
              <h1 className="page-title">
                자주 묻는
                <br />
                <span className="em">질문</span>.
              </h1>
              <p className="page-sub">
                도입 전 많이 물어보시는 질문들을 정리했습니다. 회사, 서비스, 요금, 보안까지 —
                더 궁금한 점은 언제든 물어봐 주세요.
              </p>
            </div>
            <div className="page-meta">{status === 'ready' ? `${faqs.length} Questions` : ''}</div>
          </div>
        </div>
      </section>

      <section className="faq-main">
        <div className="container reveal">
          {faqs.length > 0 ? (
            <div className="faq-toolbar">
              <ul className="faq-cats">
                {categories.map((cat) => (
                  <li key={cat.name}>
                    <button
                      type="button"
                      className={category === cat.name ? 'on' : ''}
                      onClick={() => setCategory(cat.name)}
                    >
                      {cat.name} <span className="cnt">{cat.count}</span>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="faq-search">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="질문을 검색해보세요"
                />
              </div>
            </div>
          ) : null}

          <ul className="faq-list">
            {filtered.map((faq) => (
              <li className={`faq-item${openId === faq.id ? ' open' : ''}`} key={faq.id}>
                <button
                  type="button"
                  className="faq-q"
                  onClick={() => setOpenId(openId === faq.id ? null : faq.id)}
                >
                  <span className="faq-q-text">
                    <span className="faq-cat-tag">{faq.category}</span>
                    {faq.question}
                  </span>
                  <span className="faq-icon" />
                </button>
                <div className="faq-a">
                  <div className="faq-a-inner">
                    {faq.content}
                    {faq.linkUrl && faq.linkLabel ? (
                      <>
                        <br />
                        <FaqLink faq={faq} />
                      </>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {status === 'loading' ? <div className="faq-empty">질문을 불러오는 중입니다.</div> : null}
          {status === 'error' ? (
            <div className="faq-empty">질문을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.</div>
          ) : null}
          {status === 'ready' && faqs.length === 0 ? (
            <div className="faq-empty">등록된 질문이 없습니다.</div>
          ) : null}
          {status === 'ready' && faqs.length > 0 && filtered.length === 0 ? (
            <div className="faq-empty">해당하는 질문이 없습니다.</div>
          ) : null}
        </div>
      </section>

      <section className="cant-find">
        <div className="container">
          <div className="section-head reveal">
            <span className="eyebrow">Ask us anything</span>
            <h2>
              아직 답을 <span className="em">못 찾으셨나요</span>?
            </h2>
            <p>질문을 보내주시면 저희 팀이 직접 답변드립니다.</p>
          </div>
          <div className="channels reveal">
            <a href="mailto:yeplat@naver.com" className="channel">
              <div className="label">Email</div>
              <div className="value">yeplat@naver.com</div>
              <div className="note">24시간 접수 · 영업일 24h 내 답변</div>
            </a>
            <a
              href="http://pf.kakao.com/_xmpdXX"
              target="_blank"
              rel="noopener noreferrer"
              className="channel"
            >
              <div className="label">Kakao</div>
              <div className="value">카카오채널 문의</div>
              <div className="note">pf.kakao.com/_xmpdXX</div>
            </a>
            <div className="channel channel--static">
              <div className="label">Office</div>
              <div className="value">예플랫</div>
              <div className="note">대구광역시 달성군 다사읍 달구벌대로 174길50, B202호</div>
            </div>
          </div>
        </div>
      </section>

      <section className="final-cta">
        <div className="container final-cta-inner reveal">
          <h2>
            묻는 것이,
            <br />
            <span className="em">가장 빠른 길</span>입니다.
          </h2>
          <p>부담 없이 물어보세요. 우리는 정직하게 답합니다.</p>
          <Link to="/inquiry" className="btn-cream">
            신청하기 &nbsp;→
          </Link>
        </div>
      </section>

      <ScrollToTopButton />
      <Footer />
    </div>
  );
}
